import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronDown, ChevronRight, ChevronsLeft } from 'lucide-react'
import { SupplierChangeMoney } from './SupplierChangeParts'
import { useSupplierChangesSummary, useSupplierGroupOrders } from '../../auth/hooks/useSupplierChanges'
import { useFoldedSuppliers } from '../../hooks/useFoldedSuppliers'
import { splitDateTime } from '../../utils/datetime'
import type { SupplierChangeGroup, SupplierChangeOrder } from '../../auth/types'
import {
  FOCUS_RING,
  SUPPLIER_CHANGE_TIP,
  SUPPLIER_GROUP_FIRST_PAGE,
  SUPPLIER_GROUP_MORE_STEP,
} from '../../constants/supplierChanges'

// Option J's review rail beside an order's supplier changes page: the orders
// to review, grouped by supplier (oldest change first), the open one marked
// and the rest one click away. Each group pages like the list (5, then 10
// more) and folds; folds are shared with the list. The open order always
// shows in its group, even when it isn't in the rows loaded yet.

/**
 * The open order. ``row`` shows it when its row isn't loaded in the rail;
 * it is null while the order itself is still loading.
 */
export interface RailCurrentOrder {
  id: string
  supplierId: number | null
  row: SupplierChangeOrder | null
}

export function SupplierChangesRail({ current, onHide, onPickOrder }: { current: RailCurrentOrder; onHide: () => void; onPickOrder?: () => void }) {
  const { data, isLoading, error, refetch, isFetching } = useSupplierChangesSummary('')
  const { folded, toggle } = useFoldedSuppliers()
  const groups = data?.suppliers ?? []
  return (
    <aside aria-labelledby="review-rail-title" className="flex max-h-[calc(100vh-11rem)] w-72 flex-shrink-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-100 px-3 py-2">
        <h2 id="review-rail-title" className="whitespace-nowrap text-sm font-semibold text-gray-900">
          Supplier changes to review <span className="font-normal tabular-nums text-gray-600">({data?.count ?? 0})</span>
        </h2>
        <div className="flex items-center gap-2">
          <p className="flex-1 text-xs text-gray-600" title={SUPPLIER_CHANGE_TIP}>Oldest first · supplier&apos;s change</p>
          <button type="button" onClick={onHide} aria-label="Hide supplier changes to review" className={`-mr-1.5 inline-flex flex-shrink-0 items-center gap-1 rounded-md px-1.5 py-1 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 ${FOCUS_RING}`}>
            <ChevronsLeft className="h-4 w-4" aria-hidden="true" />
            Hide
          </button>
        </div>
      </div>
      {isLoading ? (
        <p className="px-3 py-4 text-sm text-gray-500">Loading…</p>
      ) : error && !data ? (
        // A failed load is not an empty queue: say so and offer a retry.
        <div className="px-3 py-4 text-sm text-gray-600">
          <p>Couldn&apos;t load the orders to review.</p>
          <button type="button" disabled={isFetching} onClick={() => void refetch()} className={`mt-1 rounded-md text-xs font-semibold text-gray-900 underline underline-offset-2 disabled:opacity-50 ${FOCUS_RING}`}>
            {isFetching ? 'Trying again…' : 'Try again'}
          </button>
        </div>
      ) : groups.length === 0 ? (
        <p className="px-3 py-4 text-sm text-gray-600">Nothing to review.</p>
      ) : (
        // relative: the rows' screen-reader-only labels are positioned
        // absolutely; anchoring them here keeps them inside this scroll box,
        // so off-screen rows can't add blank scroll space under the page.
        <div className="relative flex-1 overflow-y-auto p-1.5">
          {groups.map((group) => {
            const foldKey = String(group.supplier_id)
            return <RailGroup key={foldKey} group={group} current={current} isFolded={folded.has(foldKey)} onToggle={() => toggle(foldKey)} onPickOrder={onPickOrder} />
          })}
        </div>
      )}
    </aside>
  )
}

function RailGroup({ group, current, isFolded, onToggle, onPickOrder }: { group: SupplierChangeGroup; current: RailCurrentOrder; isFolded: boolean; onToggle: () => void; onPickOrder?: () => void }) {
  const [limit, setLimit] = useState(SUPPLIER_GROUP_FIRST_PAGE)
  const { data, isFetching, error, refetch } = useSupplierGroupOrders(group.supplier_id, '', limit, !isFolded)
  const loaded = data?.results ?? []
  const total = data?.count ?? group.order_count
  const left = total - loaded.length
  const isCurrentGroup = current.supplierId === group.supplier_id
  const isCurrentLoaded = loaded.some((order) => order.id === current.id)
  // A folded group still shows the open order, so it's never out of reach.
  let rows = isFolded ? [] : loaded
  if (current.row && isCurrentGroup && (isFolded || !isCurrentLoaded)) rows = [...rows, current.row]
  const listId = `review-rail-${group.supplier_id}`
  return (
    <section aria-label={group.supplier_name ?? 'Supplier'} className="mt-1 first:mt-0">
      <h3>
        <button type="button" aria-expanded={!isFolded} aria-controls={listId} onClick={onToggle} className={`flex min-h-8 w-full items-center gap-1 rounded-md bg-gray-50 px-1.5 py-1 text-left text-xs font-semibold text-slate-900 transition-colors hover:bg-gray-100 ${FOCUS_RING}`}>
          {isFolded ? <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-600" aria-hidden="true" /> : <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-600" aria-hidden="true" />}
          <span className="flex-1">
            {group.supplier_name ?? 'Unknown supplier'} · {group.order_count}
            <span className="sr-only"> order{group.order_count === 1 ? '' : 's'}</span>
          </span>
          <span className="font-normal tabular-nums text-gray-600"><SupplierChangeMoney amount={group.supplier_change} /></span>
        </button>
      </h3>
      {!isFolded && error && !data && (
        // A failed load is not an empty supplier: say so and offer a retry.
        <div className="px-2.5 py-2 text-xs text-gray-600">
          <p>Couldn&apos;t load these orders.</p>
          <button type="button" disabled={isFetching} onClick={() => void refetch()} className={`mt-0.5 rounded-md font-semibold text-gray-900 underline underline-offset-2 disabled:opacity-50 ${FOCUS_RING}`}>
            {isFetching ? 'Trying again…' : 'Try again'}
          </button>
        </div>
      )}
      <ul id={listId} className="space-y-0.5">
        {rows.map((order) => <RailRow key={order.id} order={order} isCurrent={order.id === current.id} onPick={onPickOrder} />)}
      </ul>
      {!isFolded && data && left > 0 && (
        <button type="button" disabled={isFetching} onClick={() => setLimit((value) => value + SUPPLIER_GROUP_MORE_STEP)} className={`mt-0.5 w-full rounded-md px-2.5 py-1.5 text-left text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 ${FOCUS_RING}`}>
          {isFetching ? 'Loading…' : `Show ${Math.min(left, SUPPLIER_GROUP_MORE_STEP)} more (${left} left)`}
        </button>
      )}
    </section>
  )
}

function RailRow({ order, isCurrent, onPick }: { order: SupplierChangeOrder; isCurrent: boolean; onPick?: () => void }) {
  const { tenantSlug } = useParams()
  const since = order.oldest_change_at ? splitDateTime(order.oldest_change_at).date : ''
  return (
    <li>
      <Link to={`/${tenantSlug}/orders/${order.id}/supplier-changes`} onClick={onPick} aria-current={isCurrent ? 'page' : undefined} className={`block rounded-md px-2.5 py-2 transition-colors ${FOCUS_RING} ${isCurrent ? 'bg-gray-200/70' : 'hover:bg-gray-50'}`}>
        <span className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-semibold text-gray-900">{order.order_number}</span>
          <span className="text-sm font-semibold tabular-nums text-gray-900"><SupplierChangeMoney amount={order.supplier_change} /></span>
        </span>
        <span className="block truncate text-xs text-gray-600">
          {order.engineer_name}{order.branch_name ? ` · ${order.branch_name}` : ''}
        </span>
        <span className="block text-xs tabular-nums text-gray-600">
          {order.changed_line_count} open{since ? ` · since ${since}` : ''}
        </span>
      </Link>
    </li>
  )
}
